/* eslint-disable @typescript-eslint/no-explicit-any */
import {Injectable} from '@angular/core';
import {equals} from '../../../common/service/data-util';
import {AttributeViewerContext} from './attribute-viewer-context.model';

@Injectable({
    providedIn: 'root'
})
export class AttributeViewerFilterService {
    title = 'AttributeViewerFilterService';

    htmlTitleAttrRegex = /title="([a-zA-Z0-9_ ]*)"/;

    applyFilter(context: AttributeViewerContext) {
        console.debug(`${this.title} - applyFilter`);

        const selectedFeatureIds = context.selectedAttrsList.map((selectedFeatureAttributes: any) => selectedFeatureAttributes.id);

        const featureArray = context.hideUnselectedRows
            ? context.features.filter((feature: {attributes: {id: any; transientParentId: any}}) => {
                  const parentId = feature.attributes.transientParentId
                      ? this.getParentId(feature.attributes.transientParentId)
                      : feature.attributes.id;
                  return selectedFeatureIds.indexOf(parentId) !== -1;
              })
            : context.features;

        let filteredFeatureArray = featureArray.filter((feature: {attributes: {parentid: any; transientId: any; transientParentId: any}}) => {
            const filterColumns = context.options.columns.filter((column: any) => {
                const value = feature.attributes[column.prop];

                let attributeValue: string;
                if (column.prop === 'id' && context.options.enlargementMode && feature.attributes.parentid) {
                    attributeValue = `${feature.attributes.parentid}`;
                } else if (column.prop === 'type' && typeof value === 'string') {
                    const matchResult = value.match(this.htmlTitleAttrRegex);
                    attributeValue = matchResult ? matchResult[1] : value;
                } else {
                    attributeValue = `${value}`;
                }

                return column.filterValue && column.filterValue !== '' + attributeValue;
            });

            return !feature.attributes.transientParentId && filterColumns.length === 0;
        });

        const hiddenChildren = [];
        if (context.options.columns.some((column: any) => column.isTreeColumn)) {
            // Add children for all expanded parents
            const expandedParentIds = this.getExpandedParentIds(context);
            const finalResult = [];
            filteredFeatureArray.forEach((parent: {attributes: {transientId: any}}) => {
                finalResult.push(parent);
                featureArray.forEach((child: {attributes: {transientId: any; transientParentId: any}}) => {
                    if (
                        child.attributes.transientParentId &&
                        parent.attributes.transientId === child.attributes.transientParentId
                    ) {
                        if (expandedParentIds.includes(child.attributes.transientParentId)) {
                            finalResult.push(child);
                        } else {
                            hiddenChildren.push(child);
                        }
                    }
                });
            });

            filteredFeatureArray = finalResult;
        }

        context.filterApplied = filteredFeatureArray.length + hiddenChildren.length < featureArray.length;

        const filteredAttrsArray = filteredFeatureArray.map((v: any) => v.attributes);

        if (!context.attrsList || !equals(this.getSortedFeatureAttrsArray(context.attrsList), this.getSortedFeatureAttrsArray(filteredAttrsArray))) {
            // Set context.attrsList undefined to force the ngx-datatable element out of the DOM.
            context.attrsList = undefined;

            // Strip $$id attributes (added by ngx-datatable) to avoid duplicate $$id attributes
            filteredAttrsArray.filter(attrs => Object.hasOwn(attrs, '$$id')).forEach(attrs => delete attrs.$$id);

            // Reassign features back to context.attrsList after timeout to force the ngx-datatable element to be rebuilt.
            window.setTimeout(() => (context.attrsList = filteredAttrsArray), 0);
        }
    }

    hideShowUnselectedRows(context: AttributeViewerContext) {
        console.debug(`${this.title} - hideShowUnselectedRows`);

        context.hideUnselectedRows = !context.hideUnselectedRows;

        this.applyFilter(context);
    }

    clearFilter(context: AttributeViewerContext) {
        console.debug(`${this.title} - clearFilter`);

        context.options.columns.forEach((column: any) => (column.filterValue = undefined));

        this.applyFilter(context);
    }

    filterFeaturesByAttributeValue(context: AttributeViewerContext, attribute: any, viewValue: any) {
        console.debug(`${this.title} - filterFeaturesByAttributeValue`);

        const values = [];
        context.attrsList.forEach((feature: any) => {
            const value = feature[attribute.prop];

            let attributeValue: string;

            if (attribute.prop === 'id' && context.options.enlargementMode && feature.parentid) {
                attributeValue = `${feature['parentid']}`;
            } else if (attribute.prop === 'type' && typeof value === 'string') {
                const matchResult = value.match(this.htmlTitleAttrRegex);
                attributeValue = matchResult ? matchResult[1] : value;
            } else {
                attributeValue = `${value}`;
            }

            if (attributeValue.substring(0, viewValue.length).toLowerCase() === viewValue.toLowerCase() && !values.includes(attributeValue)) {
                values.push(attributeValue);
            }
        });

        return values;
    }

    getExpandedParentIds(context: AttributeViewerContext) {
        return context.features.filter(feature => feature.attributes.treeStatus === 'expanded').map(feature => feature.attributes.transientId);
    }

    private getFeatureAttrsKey(featureAttrs: any) {
        return `${featureAttrs.id}_${Object.keys(featureAttrs).sort().join()}`;
    }

    private getSortedFeatureAttrsArray(featureAttrsArray: any[]) {
        return featureAttrsArray
            .map((v: any) => structuredClone(v))
            .sort((v1: any, v2: any) => this.getFeatureAttrsKey(v1).localeCompare(this.getFeatureAttrsKey(v2)));
    }

    private getParentId(id: string) {
        return parseInt(id.replace('parent', ''));
    }
}
